import { assert, BadRequestError, InternalError } from '@fragment/error-utils';
import type { CurrencyCode } from '@fragment/types';
import type { CurrencyMatchInput } from '@fragment/types/currencyCodes';
import { CurrencyMatchInputSchema } from '@fragment/types/currencyCodes';

import type { LedgerAccount, Line } from './types';

export type CurrencyParams = {
  currency: string;
  customCurrencyId?: string;
};
export const extractCurrency = (params: CurrencyParams): string => {
  const { currency, customCurrencyId } = params;
  if (currency === 'CUSTOM') {
    assert(customCurrencyId, InternalError, {
      message: 'found currency as CUSTOM without customCurrencyId set',
    });
    return customCurrencyId;
  }
  return currency;
};

export const serializeCurrency = (params: CurrencyParams): string => {
  const { currency, customCurrencyId } = params;
  if (currency === 'CUSTOM') {
    assert(customCurrencyId, InternalError, {
      message: 'found currency as CUSTOM without customCurrencyId set',
    });
    return `CUSTOM:${customCurrencyId}`;
  }
  if (customCurrencyId) {
    return `${currency}:${customCurrencyId}`;
  }
  return currency;
};

type GetLineCurrencyParams = {
  ledgerAccounts: Record<string, LedgerAccount>;
  line: Line;
};
export const getLineCurrency = ({
  ledgerAccounts,
  line,
}: GetLineCurrencyParams) => {
  const lineAccount = ledgerAccounts[line.account.id];
  let currency: CurrencyCode | undefined;
  let customCurrencyId: string | undefined;
  if (lineAccount.currency === 'MULTI') {
    ({ currency, customCurrencyId } = line);
  } else {
    ({ currency, customCurrencyId } = lineAccount);
  }
  if (!currency) {
    // NOTE: This case should never happen due to user error, as we always
    // validate further up the stack that one of the account or line currency is
    // set.
    throw new InternalError(
      'expected currency to be set on either account or line'
    );
  }
  return { currency, customCurrencyId };
};

export const assertValidCurrency = (params: {
  code: string;
  customCurrencyId?: string;
}): CurrencyMatchInput => {
  const parseResult = CurrencyMatchInputSchema.safeParse(params);
  if (!parseResult.success) {
    throw new BadRequestError(
      `Invalid currency provided: ${JSON.stringify(params)}`
    );
  }
  return parseResult.data;
};
